import type { UserRole } from '@axioncrm/shared';

export type NavGroup = 'Overview' | 'Sales' | 'Delivery' | 'Finance' | 'People' | 'System';

export interface NavItem {
  path: string;
  label: string;
  roles: UserRole[] | 'all';
  group: NavGroup;
}

export const GROUP_ORDER: NavGroup[] = ['Overview', 'Sales', 'Delivery', 'Finance', 'People', 'System'];

/** Role → module visibility (master prompt §4.3). Admin sees everything. */
export const NAV_ITEMS: NavItem[] = [
  { path: '/', label: 'Dashboard', roles: 'all', group: 'Overview' },
  { path: '/calendar', label: 'Calendar', roles: 'all', group: 'Overview' },
  { path: '/tasks', label: 'Tasks', roles: 'all', group: 'Overview' },
  { path: '/customers', label: 'Customers', roles: ['sales', 'pmo'], group: 'Sales' },
  { path: '/contacts', label: 'Contacts', roles: ['sales', 'pmo'], group: 'Sales' },
  { path: '/leads', label: 'Leads', roles: ['sales', 'pmo'], group: 'Sales' },
  { path: '/projects', label: 'Projects', roles: ['pmo'], group: 'Delivery' },
  { path: '/inbox', label: 'Inbox', roles: ['sales', 'hr', 'recruitment', 'finance', 'pmo'], group: 'Finance' },
  { path: '/invoices', label: 'Invoices', roles: ['sales', 'finance', 'pmo'], group: 'Finance' },
  { path: '/payments', label: 'Payments', roles: ['sales', 'finance', 'pmo'], group: 'Finance' },
  { path: '/vendor-advices', label: 'Vendor Advices', roles: ['finance', 'pmo'], group: 'Finance' },
  { path: '/revenue', label: 'Revenue (Archive)', roles: ['sales', 'pmo', 'finance'], group: 'Finance' },
  { path: '/revenue2', label: 'Revenue', roles: ['sales', 'pmo', 'finance'], group: 'Finance' },
  { path: '/invoice-analytics', label: 'Invoice Analytics', roles: ['finance', 'pmo'], group: 'Finance' },
  { path: '/resource-billing', label: 'Resource Billing', roles: ['finance', 'pmo'], group: 'Finance' },
  { path: '/recruitment', label: 'Recruitment', roles: ['recruitment', 'hr', 'pmo'], group: 'People' },
  { path: '/onboarding', label: 'Onboarding', roles: ['hr', 'pmo'], group: 'People' },
  { path: '/assets', label: 'Assets', roles: ['hr', 'pmo'], group: 'People' },
  { path: '/documents', label: 'Document Management', roles: ['sales', 'pmo'], group: 'System' },
  { path: '/reports', label: 'Reports', roles: ['pmo'], group: 'System' },
  { path: '/settings', label: 'Settings', roles: [], group: 'System' },
];

export function navItemsFor(role: UserRole): NavItem[] {
  return NAV_ITEMS.filter(
    (item) => role === 'admin' || item.roles === 'all' || item.roles.includes(role),
  );
}

export function groupedNavItemsFor(role: UserRole): { group: NavGroup; items: NavItem[] }[] {
  const items = navItemsFor(role);
  return GROUP_ORDER.map((group) => ({ group, items: items.filter((i) => i.group === group) })).filter(
    (g) => g.items.length > 0,
  );
}
